import axios from 'axios';
import config from '../config/config';

const API_BASE_URL = config.API_BASE_URL;

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add token to requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Handle token refresh on 403
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 403 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const refreshToken = localStorage.getItem('refreshToken');
                if (refreshToken) {
                    const response = await axios.post(`${API_BASE_URL}/api/auth/refresh-token`, {
                        refreshToken
                    });

                    const { accessToken } = response.data.data;
                    localStorage.setItem('accessToken', accessToken);

                    originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
                    return api(originalRequest);
                }
            } catch (refreshError) {
                // Refresh failed, logout user
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('user');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    login: (email, password) =>
        axios.post(`${API_BASE_URL}/api/auth/login`, { email, password }),

    signup: (data) => {
        // Check if data is FormData (for file uploads)
        const headers = data instanceof FormData ? {
            'Content-Type': 'multipart/form-data'
        } : {};

        return axios.post(`${API_BASE_URL}/api/auth/signup`, data, { headers });
    },

    logout: (refreshToken) =>
        api.post('/api/auth/logout', { refreshToken }),

    getMe: () =>
        api.get('/api/auth/me'),

    changePassword: (currentPassword, newPassword) =>
        api.put('/api/auth/change-password', { currentPassword, newPassword }),

    forgotPassword: (email) =>
        axios.post(`${API_BASE_URL}/api/auth/forgot-password`, { email }),

    verifyResetToken: (token) =>
        axios.post(`${API_BASE_URL}/api/auth/verify-reset-token`, { token }),

    resetPassword: (token, newPassword) =>
        axios.post(`${API_BASE_URL}/api/auth/reset-password`, { token, newPassword }),

    getUsers: () =>
        api.get('/api/auth/users'),

    createUser: (data) =>
        api.post('/api/auth/users', data),

    updateUser: (id, data) =>
        api.put(`/api/auth/users/${id}`, data),

    deleteUser: (id) =>
        api.delete(`/api/auth/users/${id}`)
};

// Company API
export const companyAPI = {
    getAll: () =>
        api.get('/api/companies'),

    getById: (id) =>
        api.get(`/api/companies/${id}`),

    create: (data) =>
        api.post('/api/companies', data),

    update: (id, data) =>
        api.put(`/api/companies/${id}`, data),

    updateUserLimit: (id, maxUsers) =>
        api.put(`/api/companies/${id}/user-limit`, { max_users: maxUsers }),

    toggleStatus: (id) =>
        api.put(`/api/companies/${id}/toggle-status`),

    delete: (id) =>
        api.delete(`/api/companies/${id}`),

    getStatistics: () =>
        api.get('/api/companies/statistics/overview'),

    canAddUser: (id) =>
        api.get(`/api/companies/${id}/can-add-user`),

    getCompanyWiseReport: (page = 1, limit = 10) =>
        api.get('/api/companies/reports/company-wise', {
            params: { page, limit }
        })
};

// Instructions API
export const instructionsAPI = {
    getAll: () => api.get('/api/instructions'),
    getById: (id) => api.get(`/api/instructions/${id}`),
    create: (data) => api.post('/api/instructions', data),
    update: (id, data) => api.put(`/api/instructions/${id}`, data),
    delete: (id) => api.delete(`/api/instructions/${id}`),
};

// Staff API
export const staffAPI = {
    getAll: () => api.get('/api/staff'),
    getById: (id) => api.get(`/api/staff/${id}`),
    create: (data) => api.post('/api/staff', data),
    update: (id, data) => api.put(`/api/staff/${id}`, data),
    delete: (id) => api.delete(`/api/staff/${id}`),
};

// Items API
export const itemsAPI = {
    getAll: () => api.get('/api/items'),
    getById: (id) => api.get(`/api/items/${id}`),
    create: (data) => api.post('/api/items', data),
    update: (id, data) => api.put(`/api/items/${id}`, data),
    delete: (id) => api.delete(`/api/items/${id}`),
};

// Conversations API
export const conversationsAPI = {
    getAll: (limit = 100, source = null, whatsappNumberId = null, department = null) => {
        let url = `/api/conversations?limit=${limit}`;
        if (source) url += `&source=${source}`;
        if (whatsappNumberId) url += `&whatsapp_number_id=${whatsappNumberId}`;
        if (department) url += `&department=${department}`;
        return api.get(url);
    },
    getByUser: (userId, limit = 30, source = null) => {
        let url = `/api/conversations/${userId}?limit=${limit}`;
        if (source) url += `&source=${source}`;
        return api.get(url);
    },
    search: (query) => api.get(`/api/conversations/search?q=${query}`),
};

// Chat API
export const chatAPI = {
    sendMessage: (data) => {
        return api.post('/api/chat', data)
    }

};

// WhatsApp API
export const whatsappAPI = {
    getNumbers: (companyId = null) => {
        const url = companyId ? `/api/whatsapp/numbers?company_id=${companyId}` : '/api/whatsapp/numbers';
        return api.get(url);
    },
    createNumber: (data) => api.post('/api/whatsapp/numbers', data),
    updateNumber: (id, data) => api.put(`/api/whatsapp/numbers/${id}`, data),
    deleteNumber: (id) => api.delete(`/api/whatsapp/numbers/${id}`),
    getConversations: (whatsappNumberId = null, limit = 50) => {
        const url = whatsappNumberId
            ? `/api/whatsapp/conversations?whatsapp_number_id=${whatsappNumberId}&limit=${limit}`
            : `/api/whatsapp/conversations?limit=${limit}`;
        return api.get(url);
    },
};

// Summaries API
export const summariesAPI = {
    generate: () => api.post('/api/summaries/generate'),
    getAll: (filters = {}) => {
        const params = new URLSearchParams();
        Object.keys(filters).forEach(key => {
            if (filters[key] && filters[key] !== 'All') {
                params.append(key, filters[key]);
            }
        });
        return api.get(`/api/summaries?${params.toString()}`);
    },
    getById: (id) => api.get(`/api/summaries/${id}`),
    update: (id, data) => api.put(`/api/summaries/${id}`, data),
    delete: (id) => api.delete(`/api/summaries/${id}`),
    getStats: () => api.get('/api/summaries/stats/overview'),
};

// Status API
export const statusAPI = {
    check: () => api.get('/status'),
};

export default api;
